import { spErrorTags, structuredCloneL } from "@eventr/common";

// The identifier is the whole contract: everything else about an item's shape is the
// section's business, not the drafts store's.
export interface DraftItem {
  _id: string;
  [key: string]: unknown;
}

/**
 * A copy of node with the object matching draft._id replaced, searched anywhere within it
 * (arrays and nested objects alike), or undefined when no object matches. Only the arrays and
 * objects on the path to the match are copied; every other entry keeps its identity.
 * Mirrors getObjectById's own traversal, since a draft's _id may name an item nested
 * below the top level (e.g. a flight leg within a flight).
 */
function replaceById(node: any, draft: DraftItem): any {
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      const item = node[i];
      if (!item || typeof item !== "object") continue;
      const next = (item as DraftItem)._id === draft._id ? structuredCloneL(draft) : replaceById(item, draft);
      if (next === undefined) continue;
      const copy = node.slice();
      copy[i] = next;
      return copy;
    }
    return undefined;
  }
  if (node && typeof node === "object") {
    for (const key of Object.keys(node)) {
      const value = node[key];
      if (!value || typeof value !== "object") continue;
      const next =
        !Array.isArray(value) && (value as DraftItem)._id === draft._id
          ? structuredCloneL(draft)
          : replaceById(value, draft);
      if (next === undefined) continue;
      return { ...node, [key]: next };
    }
  }
  return undefined;
}

/** The entry of id in any of container's top-level lists, or container itself when its own _id matches. */
export function findTopLevelEntry(container: unknown, id: string): DraftItem | undefined {
  if (!container || typeof container !== "object") return undefined;
  if ((container as DraftItem)._id === id) return container as DraftItem;
  for (const value of Object.values(container)) {
    if (!Array.isArray(value)) continue;
    const found = value.find((entry) => entry && typeof entry === "object" && entry._id === id);
    if (found) return found;
  }
  return undefined;
}

/** Every _id inside a drafted item: the item itself plus nested groups and list entries. */
export function collectDraftItemIds(item: DraftItem): string[] {
  const ids: string[] = [];
  const walk = (node: any): void => {
    if (!node || typeof node !== "object") return;
    if (typeof node._id === "string" && node._id) ids.push(node._id);
    for (const value of Object.values(node)) walk(value);
  };
  walk(item);
  return ids;
}

// The one merge shared by the generic section provider and the Activities/Transport
// selectors. Reads no store and knows nothing about sections.
//
// A missing key is never guessed. Guessing took whichever top-level key enumerated first,
// appending items to an unrelated array on any container with more than one.
export function mergeDraftItems(container: any, key: string | undefined, drafts: DraftItem[] | undefined): any {
  if (!drafts?.length) return container;

  if (!key) {
    spErrorTags("mergeDraftItems: no key to merge drafts into", ["DRAFTS"], { container, drafts });
    return container;
  }

  let list: any[] = Array.isArray(container?.[key]) ? container[key] : [];

  // Top-level entries first, in one pass over the list.
  const pending = new Map(drafts.map((draft) => [draft._id, draft]));
  list = list.map((item) => {
    const draft = item && typeof item === "object" ? pending.get((item as DraftItem)._id) : undefined;
    if (!draft) return item;
    pending.delete(draft._id);
    return structuredCloneL(draft);
  });

  for (const draft of pending.values()) {
    const replaced = replaceById(list, draft);
    if (replaced !== undefined) {
      list = replaced;
      continue;
    }
    spErrorTags(`mergeDraftItems: draft not found in container, pushed as top-level entry: ${draft._id}`, ["DRAFTS"]);
    list = [...list, structuredCloneL(draft)];
  }

  return container && typeof container === "object" ? { ...container, [key]: list } : { [key]: list };
}
